/**
 * Embeddable expense panel for other modules.
 *
 * Part 10 of the expense specification asks for expense history to appear inside
 * Contacts, Projects and Employees. Rather than duplicating a list in each of
 * those features, they all mount this one component with a different scope.
 *
 * Scoping is applied as a server-side filter, so a panel can never show expenses
 * outside the caller's tenant or the requested subject.
 */
import { Link } from 'react-router-dom';
import { getApiErrorMessage } from '@/shared/api/axios';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { useExpensesV2, type ExpenseFilters, type ExpenseStatus } from '../hooks/useExpenseApi';

/** What the panel is scoped to. Exactly one subject is expected. */
export type ExpenseContextScope =
  | { kind: 'supplier'; id: string }
  | { kind: 'employee'; id: string }
  | { kind: 'project'; id: string }
  | { kind: 'department'; id: string }
  | { kind: 'costCenter'; id: string };

const STATUS_TONE: Record<ExpenseStatus, 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info'> = {
  draft: 'neutral',
  submitted: 'warning',
  approved: 'info',
  rejected: 'danger',
  posted: 'primary',
  partially_paid: 'warning',
  paid: 'success',
  cancelled: 'neutral',
  refunded: 'neutral',
};

function money(minor: number | undefined, currency = 'INR'): string {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 })
    .format((Number(minor) || 0) / 100);
}

function shortDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { month: 'short', day: '2-digit', year: '2-digit' });
}

/** Translate a scope into the API filter the server understands. */
function scopeToFilters(scope: ExpenseContextScope): ExpenseFilters {
  switch (scope.kind) {
    case 'supplier': return { supplierId: scope.id };
    case 'employee': return { employeeId: scope.id };
    case 'project': return { projectId: scope.id };
    case 'department': return { departmentId: scope.id };
    case 'costCenter': return { costCenterId: scope.id };
  }
}

const SCOPE_LABEL: Record<ExpenseContextScope['kind'], string> = {
  supplier: 'supplier',
  employee: 'employee',
  project: 'project',
  department: 'department',
  costCenter: 'cost centre',
};

export interface ExpenseContextPanelProps {
  scope: ExpenseContextScope;
  /** Heading shown above the list. */
  title?: string;
  /** How many rows to show before linking out to the full list. */
  limit?: number;
  /** Hide the roll-up strip when the host screen already shows totals. */
  showTotals?: boolean;
}

export function ExpenseContextPanel({
  scope,
  title = 'Expenses',
  limit = 10,
  showTotals = true,
}: ExpenseContextPanelProps) {
  const filters: ExpenseFilters = { ...scopeToFilters(scope), pageSize: limit };
  const { data, isLoading, isError, error } = useExpensesV2(filters);

  if (isLoading) {
    return (
      <Card>
        <CardBody className="space-y-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </CardBody>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardBody>
          <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
          <p className="mt-1 text-sm text-rose-600">Could not load expenses.</p>
          <p className="text-xs text-slate-500">{getApiErrorMessage(error) || 'Please retry in a moment.'}</p>
        </CardBody>
      </Card>
    );
  }

  const rows = data?.items ?? [];
  const total = data?.meta?.total ?? rows.length;

  // Roll-ups are computed from the returned page only, and labelled as such, so
  // the figures cannot be mistaken for a full-history total.
  const pageGross = rows.reduce((sum, e) => sum + e.grossAmountMinor, 0);
  const pageOutstanding = rows.reduce((sum, e) => sum + e.outstandingMinor, 0);

  return (
    <Card>
      <CardBody>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
          {total > 0 ? (
            <Link
              to={`/accounting-v2/expenses`}
              className="text-xs text-indigo-600 hover:underline"
            >
              View all {total}
            </Link>
          ) : null}
        </div>

        {rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">
            No expenses recorded against this {SCOPE_LABEL[scope.kind]} yet.
          </p>
        ) : (
          <>
            {showTotals ? (
              <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Shown</p>
                  <p className="text-sm font-semibold text-slate-800">{money(pageGross)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Outstanding</p>
                  <p className="text-sm font-semibold text-slate-800">{money(pageOutstanding)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Records</p>
                  <p className="text-sm font-semibold text-slate-800">
                    {rows.length}
                    {total > rows.length ? <span className="text-xs font-normal text-slate-400"> of {total}</span> : null}
                  </p>
                </div>
              </div>
            ) : null}

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200">
                  <tr>
                    <th className="px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">Expense</th>
                    <th className="px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">Date</th>
                    <th className="px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">Amount</th>
                    <th className="px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">Outstanding</th>
                    <th className="px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => (
                    <tr key={e._id} className="border-b border-slate-100">
                      <td className="px-2 py-1.5">
                        <Link to={`/accounting-v2/expenses/${e._id}`} className="font-mono text-xs text-indigo-600 hover:underline">
                          {e.expenseNo}
                        </Link>
                        {e.description ? <p className="text-xs text-slate-500">{e.description}</p> : null}
                      </td>
                      <td className="px-2 py-1.5 text-xs text-slate-600">{shortDate(e.expenseDate)}</td>
                      <td className="px-2 py-1.5 font-medium">{money(e.grossAmountMinor, e.currency)}</td>
                      <td className="px-2 py-1.5">
                        {e.outstandingMinor > 0 ? money(e.outstandingMinor, e.currency) : <span className="text-xs text-slate-400">settled</span>}
                      </td>
                      <td className="px-2 py-1.5">
                        <Badge tone={STATUS_TONE[e.status] ?? 'neutral'}>{e.status.replace(/_/g, ' ')}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

export default ExpenseContextPanel;
